import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { IAhuUnit } from 'models';
import { ahuDisplayTag, isAhuRowDisabled } from '../../ahuUtils';
import type { AhuDragProps } from '../../hooks/useAhuDragOrder';

export type AhuRowAction =
  | 'edit'
  | 'rename'
  | 'duplicate'
  | 'create-version'
  | 'delete'
  | 'make-main';

interface AhuRowProps {
  unit: IAhuUnit;
  /** Global render index (project_{{index}} / data-index). */
  index: number;
  isVersion: boolean;
  /** Main rows only: render the versions caret + container hooks. */
  hasVersions: boolean;
  expanded: boolean;
  selected: boolean;
  /** Rename-in-place unlocked for this row (B1.6 / parity 145). */
  renaming: boolean;
  /** `.permission_check` lock per action (parity 135): '' or ' non-clickable'. */
  permCheckClass: (action: Exclude<AhuRowAction, 'edit'>) => string;
  onRowClick: (id: number) => void;
  /** dblclick row / Edit item → /ahu/detail/{id} same tab (parity 139). */
  onOpenAhu: (id: number) => void;
  onToggleVersions: (id: number) => void;
  onAction: (action: AhuRowAction, id: number) => void;
  /** Rename commit (change event): <3 trimmed chars reverts, else PATCH. */
  onRenameCommit: (id: number, value: string) => void;
  onRenameEnd: () => void;
  dragProps?: AhuDragProps;
  dragging?: boolean;
}

const HOVER_OPEN_MS = 500;

/**
 * One AHU row (`ahus/tmpl/project_ahu_unit.tmpl`, B1.3) — markup faithful:
 * `.list-inline.col-xs-12.show_ahu_data.project_{i}[.is-version]
 * [.is-static-version][.non-clickable]` wrapping the `.left-bar-lists` row
 * (name input + badges + caret) and the `.table-buttons.ctrl-btns` actions
 * dropdown (`.none` while unfinished — parity 133).
 *
 * Menu behaviors (B1.4 / parity 140): click toggles; hovering the
 * more-actions button 500ms auto-opens; `.top` on the menu when fewer than
 * 230px remain below in `.left-column-proposal-scrollable` (event_focus);
 * inline `top` repositioning per open_editing_popup (negative near the
 * viewport bottom, else 25px); outside click closes.
 */
const AhuRow = ({
  unit,
  index,
  isVersion,
  hasVersions,
  expanded,
  selected,
  renaming,
  permCheckClass,
  onRowClick,
  onOpenAhu,
  onToggleVersions,
  onAction,
  onRenameCommit,
  onRenameEnd,
  dragProps,
  dragging,
}: AhuRowProps) => {
  const displayTag = ahuDisplayTag(unit);
  const disabled = isAhuRowDisabled(unit);
  const finished = !!unit.finished;

  const inputRef = useRef<HTMLInputElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLUListElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [menuOpen, setMenuOpen] = useState(false);
  const [tagValue, setTagValue] = useState(displayTag);

  // Follow server renames while NOT renaming (socket put patcher etc.).
  useEffect(() => {
    if (!renaming) setTagValue(displayTag);
  }, [displayTag, renaming]);

  // renameAhu: strip readonly, re-set value (caret to end), focus (B1.6).
  useEffect(() => {
    if (renaming && inputRef.current) {
      const el = inputRef.current;
      el.focus();
      const len = el.value.length;
      el.setSelectionRange(len, len);
    }
  }, [renaming]);

  // Outside click closes the open menu (bootstrap dropdown document handler).
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDocClick = (e: MouseEvent) => {
      if (!dropdownRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [menuOpen]);

  // shown.bs.dropdown → open_editing_popup: position the .edit-popup; plus the
  // event_focus `.top` flip (pos < 230 in the scrollable column).
  useLayoutEffect(() => {
    if (!menuOpen) return;
    const btn = buttonRef.current;
    const menu = menuRef.current;
    if (!btn || !menu) return;
    const scrollable = document.querySelector(
      '#main_ahus .left-column-proposal-scrollable'
    );
    const btnRect = btn.getBoundingClientRect();
    if (scrollable) {
      const scRect = scrollable.getBoundingClientRect();
      const pos = scrollable.clientHeight - btnRect.top + (scRect.top + window.scrollY);
      if (pos < 230) menu.classList.add('top');
      else menu.classList.remove('top');
    }
    const windowH = document.body.getBoundingClientRect().height;
    const menuH = menu.getBoundingClientRect().height;
    if (windowH - btnRect.bottom - 55 < menuH) {
      menu.style.top = `${-(menuH - (windowH - btnRect.bottom) + 40)}px`;
    } else {
      menu.style.top = '25px';
    }
  }, [menuOpen]);

  useEffect(
    () => () => {
      if (hoverTimer.current) clearTimeout(hoverTimer.current);
    },
    []
  );

  const commitRename = () => {
    if (!renaming) return;
    onRenameCommit(unit.id, tagValue);
    onRenameEnd();
  };

  const menuItem = (action: AhuRowAction) => {
    setMenuOpen(false);
    onAction(action, unit.id);
  };

  // Legacy renders the name input with the literal attribute readonly="readonly"
  // (project_ahu_unit.tmpl:7,10), which the project sheet matches with
  // `.ahu-name-input[readonly="readonly"] { pointer-events: none;
  // user-select: none; }` (_ahus.scss:205-209) — the non-renaming input is
  // click-through, so row click/dblclick/drag all land on the row itself.
  // React's readOnly prop renders readonly="" and the value-keyed selector
  // misses, leaving the input interactive (press over the name starts a text
  // selection instead of grabbing the row — QA batch 3 #2). Inline the same
  // two declarations while readonly; rename (renaming=true) restores editing.
  const readonlyClickThrough = {
    pointerEvents: 'none',
    userSelect: 'none',
  } as const;

  const outerClass =
    `list-inline col-xs-12 show_ahu_data project_${index}` +
    `${isVersion ? ' is-version' : ''}` +
    `${unit.static_version ? ' is-static-version' : ''}` +
    `${disabled ? ' non-clickable ' : ''}` +
    `${dragging ? ' dragging' : ''}`;

  return (
    <div
      className={outerClass}
      {...(isVersion ? { 'data-parent-id': unit.parent_version_id ?? undefined } : {})}
      {...(isVersion && dragProps ? dragProps : {})}
    >
      <div
        data-index={index}
        className={`row left-bar-lists project_${index} project_id_${unit.id}${
          isVersion ? ' left-bar-lists-version' : ''
        }${selected ? ' selected-row' : ''}`}
        data-id={unit.id}
        id={String(unit.id)}
        data-ahu-id={`project_${unit.id}`}
        {...(isVersion ? { 'data-version-of': unit.parent_version_id ?? undefined } : {})}
        onClick={() => onRowClick(unit.id)}
        onDoubleClick={(e) => {
          const target = e.target as HTMLElement;
          // dblclick on a writable rename input does not navigate (parity 139).
          if (target.tagName === 'INPUT' && !(target as HTMLInputElement).readOnly)
            return;
          onOpenAhu(unit.id);
        }}
      >
        <div className="col-xs-12 top">
          <div className="">
            <span className="ahu-info too_long_text">
              {unit.static_version ? (
                <>
                  <input
                    value={displayTag}
                    name="tag"
                    className="ahu-name-input too_long_text"
                    data-id={unit.id}
                    readOnly
                    style={readonlyClickThrough}
                    maxLength={30}
                  />
                  {/* Whitespace text node before the badge — legacy
                      project_ahu_unit.tmpl:7-8 has a newline here; JSX strips
                      it, shifting the badge ~3px left (badge margin-left is 6px,
                      _main.scss:1069-1071). Restore the inter-element space. */}
                  {' '}
                  <span
                    className="ahu-static-badge"
                    title="Static version (non-editable)"
                  >
                    static
                  </span>
                </>
              ) : (
                <>
                  <input
                    ref={inputRef}
                    value={tagValue}
                    name="tag"
                    className={`ahu-name-input too_long_text${
                      !finished ? ' input-fit-content' : ''
                    }`}
                    data-id={unit.id}
                    maxLength={30}
                    readOnly={!renaming}
                    style={renaming ? undefined : readonlyClickThrough}
                    onChange={(e) => setTagValue(e.target.value)}
                    onBlur={commitRename}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                    }}
                  />
                  {/* Whitespace text node before the badge/span — legacy
                      project_ahu_unit.tmpl:10-14 newline; JSX strips it, shifting
                      the editable badge ~3px left (_main.scss:1069-1071). */}
                  {' '}
                  {isVersion ? (
                    <span className="ahu-editable-badge">editable</span>
                  ) : (
                    <span
                      className={`ahu-duplicating-span${
                        !finished ? ' non-clickable' : ''
                      }`}
                    >
                      {!finished ? ' duplicating ...' : ''}
                    </span>
                  )}
                </>
              )}
            </span>
            {hasVersions && (
              <span
                className={`ahu-versions-toggle${expanded ? ' expanded' : ''}`}
                data-id={unit.id}
                tabIndex={-1}
                aria-label="Toggle versions"
                title="Show / hide versions"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleVersions(unit.id);
                }}
              >
                <i className="ahu-versions-caret" />
              </span>
            )}
          </div>
        </div>
      </div>

      {/* {{#if permission}} — always-true array in the live app */}
      <div
        className={`dropdown dropdownprop-format text-right table-buttons ctrl-btns${
          !finished ? ' none' : ''
        }`}
      >
        <div ref={dropdownRef} className={`with-border-radius${menuOpen ? ' open' : ''}`}>
          <button
            ref={buttonRef}
            className="btn dropdown-toggle icon-more-actions btn-more"
            type="button"
            data-toggle="dropdown"
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen((open) => !open);
            }}
            onMouseOver={(e) => {
              // 500ms hover auto-open (B1.4 / parity 140).
              const target = e.currentTarget;
              if (hoverTimer.current) clearTimeout(hoverTimer.current);
              hoverTimer.current = setTimeout(() => {
                if (document.activeElement !== target) {
                  target.focus();
                  setMenuOpen(true);
                }
              }, HOVER_OPEN_MS);
            }}
            onMouseLeave={() => {
              if (hoverTimer.current) clearTimeout(hoverTimer.current);
            }}
          />
          <ul ref={menuRef} className="dropdown-menu edit-popup">
            {isVersion ? (
              <>
                <li>
                  <a
                    tooltip="Convert to Main"
                    data-id={unit.id}
                    className={`ka-tooltip-show-after make-main-version permission_check${permCheckClass(
                      'make-main'
                    )}`}
                    tabIndex={-1}
                    onClick={() => menuItem('make-main')}
                  >
                    <i className="material-symbols-outlined convert-to-main">
                      {'\uf41f'}
                    </i>
                    Convert to Main
                  </a>
                </li>
                <li>
                  <a
                    tooltip={unit.static_version ? 'Open' : 'Edit'}
                    data-id={unit.id}
                    className="ka-tooltip-show-after edit-ahu"
                    tabIndex={-1}
                    onClick={() => menuItem('edit')}
                  >
                    <i className="icon-edit" />
                    {unit.static_version ? 'Open' : 'Edit'}
                  </a>
                </li>
                {!unit.static_version && (
                  <li>
                    <a
                      tooltip="Rename"
                      data-id={unit.id}
                      className={`ka-tooltip-show-after rename-ahu permission_check${permCheckClass(
                        'rename'
                      )}`}
                      tabIndex={-1}
                      onClick={() => menuItem('rename')}
                    >
                      {/* project_ahu_unit.tmpl:46-47 breaks the line between
                          </svg> and "Rename", so HTML collapses it to ONE
                          space. The `<i>` items sit flush against their text
                          (:35,40,53,58) and get `padding-right:10px`
                          (project/styles.css:2378-2384); the svg matches
                          neither, so JSX dropping this newline is what pulls
                          Rename — and only Rename — left of the other four. */}
                      <RenameIcon />{' '}
                      Rename
                    </a>
                  </li>
                )}
                <li>
                  <a
                    tooltip="Duplicate"
                    data-id={unit.id}
                    className={`ka-tooltip-show-after duplicate-ahu permission_check${permCheckClass(
                      'duplicate'
                    )}`}
                    tabIndex={-1}
                    onClick={() => menuItem('duplicate')}
                  >
                    <i className="icon-copy" />
                    Duplicate
                  </a>
                </li>
                <li>
                  <a
                    tooltip="Delete"
                    data-id={unit.id}
                    className={`ka-tooltip-show-after delete-ahu permission_check${permCheckClass(
                      'delete'
                    )}`}
                    tabIndex={-1}
                    onClick={() => menuItem('delete')}
                  >
                    <i className="icon-trash" />
                    Delete
                  </a>
                </li>
              </>
            ) : (
              <>
                <li>
                  <a
                    tooltip="Edit"
                    data-id={unit.id}
                    className="ka-tooltip-show-after edit-ahu"
                    tabIndex={-1}
                    onClick={() => menuItem('edit')}
                  >
                    <i className="icon-edit" />
                    Edit
                  </a>
                </li>
                <li>
                  <a
                    tooltip="Rename"
                    data-id={unit.id}
                    className={`ka-tooltip-show-after rename-ahu permission_check${permCheckClass(
                      'rename'
                    )}`}
                    tabIndex={-1}
                    onClick={() => menuItem('rename')}
                  >
                    {/* Same collapsed newline as the version menu above —
                        project_ahu_unit.tmpl:69-70. */}
                    <RenameIcon />{' '}
                    Rename
                  </a>
                </li>
                <li>
                  <a
                    tooltip="Duplicate"
                    data-id={unit.id}
                    className={`ka-tooltip-show-after duplicate-ahu permission_check${permCheckClass(
                      'duplicate'
                    )}`}
                    tabIndex={-1}
                    onClick={() => menuItem('duplicate')}
                  >
                    <i className="icon-copy" />
                    Duplicate
                  </a>
                </li>
                <li>
                  <a
                    tooltip="Create Version"
                    data-id={unit.id}
                    className={`ka-tooltip-show-after create-version permission_check${permCheckClass(
                      'create-version'
                    )}`}
                    tabIndex={-1}
                    onClick={() => menuItem('create-version')}
                  >
                    <i className="icon-save_static_version" />
                    Create Version
                  </a>
                </li>
                <li>
                  <a
                    tooltip="Delete"
                    data-id={unit.id}
                    className={`ka-tooltip-show-after delete-ahu permission_check${permCheckClass(
                      'delete'
                    )}`}
                    tabIndex={-1}
                    onClick={() => menuItem('delete')}
                  >
                    <i className="icon-trash" />
                    Delete
                  </a>
                </li>
              </>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
};

/** Inline SVG pencil — byte-faithful path from project_ahu_unit.tmpl. */
const RenameIcon = () => (
  <svg className="rename-icon" viewBox="0 0 24 24" width="24" height="20">
    <path
      fill="#9e9e9e"
      d="M15 16L11 20H21V16H15M12.06 7.19L3 16.25V20H6.75L15.81 10.94L12.06 7.19M18.71 8.04C19.1 7.65 19.1 7 18.71 6.63L16.37 4.29C16.17 4.09 15.92 4 15.66 4C15.41 4 15.15 4.1 14.96 4.29L13.13 6.12L16.88 9.87L18.71 8.04Z"
    />
  </svg>
);

export default AhuRow;
